import { isFulfilled } from "@reduxjs/toolkit";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import CommentHistoryList from "../../../common/components/functional/comments/CommentHistoryList";
import { CommentDisplay } from "../../../common/components/functional/comments/types";
import { OrderBundleComment, UpdateOrderBundleComment } from "../orderAPI";
import {
  removeOrderBundleComment,
  updateOrderBundleComment,
} from "../orderBundleCommentsSlice";

type Props = {
  comments: OrderBundleComment[];
  currentUserId: string | null;
  onCommentsUpdated: (comments: OrderBundleComment[]) => void;
};

const OrderBundleCommentHistory = ({
  comments,
  currentUserId,
  onCommentsUpdated,
}: Props) => {
  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();

  const displayComments: CommentDisplay[] = comments.map((c) => ({
    id: c.id,
    version: c.version,
    authorId: c.authorId,
    authorName: c.authorName,
    dateTime: c.dateTime,
    comment: c.comment,
  }));

  const handleEdit = async (id: string, newText: string): Promise<boolean> => {
    const comment = comments.find((c) => c.id === id);
    if (!comment) return false;

    const payload: UpdateOrderBundleComment = {
      id: comment.id,
      version: comment.version,
      bundleId: comment.bundleId,
      comment: newText,
    };

    const action = await dispatch(updateOrderBundleComment(payload));
    if (isFulfilled(action)) {
      const event = action.payload[0];
      onCommentsUpdated(
        comments.map((c) =>
          c.id === id
            ? { ...c, version: event.version, comment: event.payload.comment }
            : c,
        ),
      );
      return true;
    }
    return false;
  };

  const handleDelete = async (id: string): Promise<boolean> => {
    const comment = comments.find((c) => c.id === id);
    if (!comment) return false;

    const action = await dispatch(
      removeOrderBundleComment({ id: comment.id, version: comment.version }),
    );
    if (isFulfilled(action)) {
      onCommentsUpdated(comments.filter((c) => c.id !== id));
      return true;
    }
    return false;
  };

  return (
    <CommentHistoryList
      comments={displayComments}
      currentUserId={currentUserId}
      onEdit={handleEdit}
      onDelete={handleDelete}
      deleteConfirmationText={t("deleteCommentApproval")}
    />
  );
};

export default OrderBundleCommentHistory;
